/**
 * Contrato de la importación de extractos bancarios.
 */
import { z } from 'zod';
import { zDateISO, zPesos, zUuid, strictObject } from './common.js';
/** Qué columna del archivo es cada cosa. */
export const zImportField = z.enum([
    'date',
    'amount',
    'description',
    'debit',
    'credit',
    'balance',
    'ignore',
]);
export const zImportPreviewRequest = z
    .object({
    /** Contenido del archivo, como texto. */
    content: z.string().min(1).max(4_000_000),
    /** Si la primera fila son los títulos de las columnas. */
    hasHeader: z.boolean().default(true),
})
    .strict();
export const zImportPreviewRow = z
    .object({
    /** Número de línea tal y como lo ve el usuario en su hoja de cálculo. */
    line: z.number().int().min(1),
    date: zDateISO.nullable(),
    amount: zPesos.nullable(),
    description: z.string(),
    balance: zPesos.nullable(),
    /** Por qué no se puede importar esta fila. Vacío si está bien. */
    problems: z.array(z.string()),
})
    .strict();
export const zImportPreview = z
    .object({
    headers: z.array(z.string()),
    mapping: z.array(zImportField),
    totalRows: z.number().int().min(0),
    validRows: z.number().int().min(0),
    problemRows: z.number().int().min(0),
    rows: z.array(zImportPreviewRow),
    hasHeader: z.boolean(),
})
    .strict();
export const zImportCommitRequest = z
    .object({
    content: z.string().min(1).max(4_000_000),
    hasHeader: z.boolean().default(true),
    mapping: z.array(zImportField).min(1).max(60),
    /** Cuenta a la que pertenece el extracto. */
    accountId: zUuid,
    /** Categoría que se pone a todo lo importado. Se puede cambiar después. */
    defaultCategoryId: zUuid.nullish(),
    /** Saltar los movimientos que ya existen. */
    skipDuplicates: z.boolean().default(true),
    fileName: z.string().trim().max(300).optional(),
})
    .strict();
export const zImportCommitResult = z
    .object({
    batchId: zUuid,
    accountName: z.string(),
    totalRows: z.number().int().min(0),
    imported: z.number().int().min(0),
    duplicates: z.number().int().min(0),
    problems: z.number().int().min(0),
    /** Detalle de las filas que no se pudieron leer, para poder corregirlas. */
    problemsDetail: z.array(z.object({ line: z.number().int(), problems: z.array(z.string()) }).strict()),
})
    .strict();
export const zImportBatchSummary = z
    .object({
    id: zUuid,
    fileName: z.string().nullable(),
    accountId: zUuid.nullable(),
    accountName: z.string().nullable(),
    totalRows: z.number().int().min(0),
    imported: z.number().int().min(0),
    duplicates: z.number().int().min(0),
    problems: z.number().int().min(0),
    status: z.enum(['APPLIED', 'UNDONE']),
    undoneAt: z.string().nullable(),
    createdAt: z.string(),
})
    .strict();
// ---------------------------------------------------------------------------
//  Copiloto
// ---------------------------------------------------------------------------
/**
 * Una cifra que respalda la respuesta.
 *
 * Se devuelven siempre, incluso cuando la respuesta es corta. Es lo que permite al
 * usuario comprobar de dónde salió cada número, y lo que distingue un copiloto que
 * consulta datos de uno que se los inventa.
 */
export const zCopilotSource = z
    .object({
    label: z.string(),
    value: zPesos,
    detail: z.string().optional(),
})
    .strict();
export const zCopilotAnswer = z
    .object({
    question: z.string(),
    /** Si se entendió la pregunta. Cuando es falso, se ofrecen sugerencias. */
    understood: z.boolean(),
    answer: z.string(),
    /** Período entendido, en palabras («este mes», «la semana pasada»). */
    period: z.string(),
    intent: z.string(),
    sources: z.array(zCopilotSource),
    suggestions: z.array(z.string()),
})
    .strict();
export const zCopilotAskRequest = z
    .object({
    question: z.string().trim().min(2).max(300),
})
    .strict();
// ---------------------------------------------------------------------------
//  Restaurar una copia de seguridad
// ---------------------------------------------------------------------------
/**
 * Qué hacer con lo que ya hay en la cuenta.
 *
 * Son las dos únicas opciones sensatas, y se llaman así porque el usuario tiene que
 * poder elegir sabiendo qué pasa:
 *
 *  · `replace` — **reemplazar**: se borra lo que hay y se pone lo del archivo.
 *  · `merge` — **añadir**: se conserva lo que hay y se suma lo del archivo.
 *
 * No hay una tercera opción «inteligente» que decida sola. Restaurar una copia encima
 * de datos que ya existen es de las pocas operaciones que pueden destruir trabajo de
 * meses, y la decisión no puede tomarla el programa.
 */
export const zRestoreMode = z.enum(['replace', 'merge']);
export const zBackupRestore = strictObject({
    /** El contenido del archivo de respaldo, tal cual se descargó. */
    backup: z.record(z.unknown()),
    mode: zRestoreMode.default('merge'),
});
/** Lo que se restauró, entidad por entidad. */
export const zRestoreResult = strictObject({
    mode: zRestoreMode,
    restored: z.record(z.number()),
    /** Registros que no se pudieron restaurar por venir rotos o incompletos. */
    skipped: z.number().int(),
    /** Categorías del sistema que ya existían. No se tocan y **no son un error**. */
    alreadyPresent: z.number().int(),
    /** Cuándo se exportó el archivo que se acaba de restaurar. */
    exportedAt: z.string().nullable(),
});
//# sourceMappingURL=import.js.map